import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { checklistInspect } from '../../../constants/routes';

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function formatWhen(row) {
  const t = row.inspected_at || row.created_at;
  if (!t) return '—';
  return new Date(t).toLocaleString();
}

function ChecklistReports() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filterProperty, setFilterProperty] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [pRes, tRes] = await Promise.all([
          axios.get('/api/checklist/picker/properties', { headers: authHeader() }),
          axios.get('/api/checklist/templates', { headers: authHeader() }),
        ]);
        if (cancel) return;
        setProperties(pRes.data);
        setTemplates(tRes.data);
        const params = {};
        if (filterProperty) params.propertyId = filterProperty;
        if (filterTemplate) params.templateId = filterTemplate;
        if (filterStatus) params.status = filterStatus;
        const res = await axios.get('/api/checklist/inspections', {
          headers: authHeader(),
          params,
        });
        if (!cancel) setRows(res.data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.error || e.message || 'Failed to load');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [filterProperty, filterTemplate, filterStatus]);

  return (
    <div className="app-shell-page">
      <div className="app-page-header mb-4">
        <h2 className="h5 mb-1">Reports</h2>
        <p className="text-muted small mb-0">Filter and open inspections.</p>
      </div>
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card app-card mb-4">
        <div className="card-body">
      <div className="row g-2">
        <div className="col-md-4">
          <label className="form-label small mb-0">Property</label>
          <select
            className="form-select form-select-sm"
            value={filterProperty}
            onChange={(e) => setFilterProperty(e.target.value)}
          >
            <option value="">All</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small mb-0">Checklist</label>
          <select
            className="form-select form-select-sm"
            value={filterTemplate}
            onChange={(e) => setFilterTemplate(e.target.value)}
          >
            <option value="">All</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small mb-0">Status</label>
          <select
            className="form-select form-select-sm"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
          </select>
        </div>
      </div>
        </div>
      </div>

      {loading ? (
        <div className="app-loading text-muted" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span>Loading…</span>
        </div>
      ) : (
        <motion.div
          key={`rep-${filterProperty}-${filterTemplate}-${filterStatus}`}
          initial={reduceMotion ? false : { opacity: 0.9, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.25, 0.1, 0.25, 1] }}
        >
        <div className="card app-card">
          <div className="card-body p-0">
        <div className="app-table-wrap table-responsive">
          <table className="table table-striped table-hover table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th>Property</th>
                <th>Date</th>
                <th>Inspector</th>
                <th>Status</th>
                <th>Checklist</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4 small app-empty-hint">
                    No inspections found
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.property_name}</td>
                    <td>{formatWhen(r)}</td>
                    <td>{r.inspector_name || '—'}</td>
                    <td>
                      <span
                        className={`badge ${r.status === 'submitted' ? 'bg-success' : 'bg-warning text-dark'}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td>{r.template_name}</td>
                    <td>
                      {r.status === 'submitted' ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`${checklistInspect(r.id)}?view=1`)}
                        >
                          View
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => navigate(checklistInspect(r.id))}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          </div>
        </div>
        </motion.div>
      )}
    </div>
  );
}

export default ChecklistReports;
